import { motion } from "framer-motion";
import { Flame, Check, TrendingUp, Wallet, Target, ArrowRight, Calendar } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";

const PAINS = [
  { icon: Wallet, title: "Stop paying agencies ₹40–60k a month", body: "Learn to run your own Meta ads in-house — the money you save in 2 months pays for the whole diploma." },
  { icon: Target, title: "Know exactly where every rupee goes", body: "Audience research, creatives, retargeting and ROAS scaling — you control the funnel from click to sale." },
  { icon: TrendingUp, title: "Scale on your terms", body: "Live campaign labs with real budgets, reviewed personally by Deepankshi — so you scale with confidence, not guesswork." },
];

export const BrandOwners = ({ courses, onCourse, onEnroll }) => {
  const course = courses.find((c) => c.id === "meta-ads");
  if (!course) return null;
  return (
    <section id="brand-owners" className="py-20 sm:py-28 lg:py-36 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto" data-testid="brand-owners-section">
      <SectionHead
        overline="For brand & business owners"
        title={<>Your brand deserves an owner who <em className="text-gold-gradient not-italic">understands ads.</em></>}
        sub="Coaches, D2C founders, clinics, salons, real-estate, e-commerce — if you sell something, this is the one program that pays for itself."
      />

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
        <div className="lg:col-span-5 space-y-4">
          {PAINS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <div className="group flex gap-5 rounded-2xl border border-white/10 bg-surface/40 p-6 transition-colors duration-300 hover:border-gold/40" data-testid={`brand-owner-point-${i}`}>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gold/30 bg-gold/10 text-gold">
                  <p.icon size={18} />
                </span>
                <div>
                  <h3 className="font-display text-lg sm:text-xl text-neutral-100 group-hover:text-champagne transition-colors">{p.title}</h3>
                  <p className="mt-2 text-sm font-light leading-relaxed text-neutral-400">{p.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1} className="lg:col-span-7">
          <motion.div
            whileHover={{ y: -4 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="relative overflow-hidden rounded-3xl border border-gold/40 bg-gradient-to-br from-gold/[0.14] via-elevated to-elevated p-7 sm:p-10 shadow-[0_40px_100px_-30px_rgba(232,193,90,0.35)]"
            data-testid="brand-owners-bestseller-card"
          >
            <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-gold/25 blur-3xl" />
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-gold px-3.5 py-1.5 font-sans text-[11px] font-semibold text-void">
                <Flame size={12} /> Bestseller
              </span>
              <span className="rounded-full border border-white/15 px-3.5 py-1.5 font-sans text-xs text-neutral-300">
                {course.type} · {course.duration}
              </span>
            </div>

            <h3 className="mt-6 font-display text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.1] text-neutral-100">{course.title}</h3>
            <p className="mt-3 text-sm sm:text-base font-light text-neutral-400">Live on Google Meet · Certificate & Diploma · Free freelancing training included</p>

            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {course.benefits.map((b) => (
                <li key={b} className="flex gap-3 text-sm text-neutral-300 leading-relaxed">
                  <Check size={15} className="mt-0.5 shrink-0 text-gold" /> {b}
                </li>
              ))}
            </ul>

            <div className="mt-8 grid grid-cols-3 gap-3">
              {[
                { label: "Metro jobs", value: course.salary },
                { label: "Freelance", value: course.freelance },
                { label: "Duration", value: course.duration },
              ].map((s) => (
                <div key={s.label} className="rounded-xl border border-white/10 bg-void/50 p-4">
                  <p className="font-sans text-xs text-neutral-500">{s.label}</p>
                  <p className="mt-1.5 font-display text-base sm:text-lg text-champagne">{s.value}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-5 border-t border-white/10 pt-7">
              <div>
                <p className="font-display text-4xl text-champagne">₹{course.price.toLocaleString("en-IN")}</p>
                <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-neutral-500"><Calendar size={12} /> {course.type} · live on Google Meet · limited seats</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <motion.button
                  data-testid="brand-owners-enroll-button"
                  onClick={() => onEnroll(course)}
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  className="btn-shine inline-flex items-center gap-2 rounded-full bg-leaf px-7 py-3.5 text-sm font-semibold text-void hover:bg-champagne transition-colors"
                >
                  Enroll Now <ArrowRight size={15} />
                </motion.button>
                <button
                  data-testid="brand-owners-details-button"
                  onClick={() => onCourse(course)}
                  className="rounded-full border border-white/20 px-6 py-3.5 text-sm text-neutral-200 hover:border-gold/60 hover:text-champagne transition-colors"
                >
                  View Full Curriculum
                </button>
              </div>
            </div>
          </motion.div>
        </Reveal>
      </div>
    </section>
  );
};
